import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  AlertCircle,
  Copy,
  Eye,
  EyeOff,
  QrCode,
  Clock,
  Smartphone,
  Loader2,
  Wifi,
  WifiOff,
  ArrowLeft,
  Settings,
  Shield,
  Info,
  Bot,
  Book,
  Check,
  Edit,
  X,
  RefreshCw,
  FileText,
  Globe,
  Sparkles,
} from "lucide-react";
import SafeModeSwitch from "../components/SafeModeSwitch";
import Select from "react-select";
import AiReplyScheduleField from "../components/configuration/AiReplyScheduleField";

const DEFAULT_KNOWLEDGE_BASE = `Blutec Ping is an AI-powered WhatsApp bulk messaging and customer engagement platform designed to automate and scale your business communications.

Blutec Ping helps businesses send WhatsApp campaigns, automate replies with AI bots, track engagement, and boost sales and customer support effectiveness—all without coding. This makes it ideal for marketing, sales, and support teams looking to improve communication efficiency and customer experience.

This contact info and website link can be shared in your product descriptions, promotional content, or customer outreach materials to enable easy access and support for interested users.

For more information or to get started, visit the official website: ping.blutec.ai
Contact us:
Email: business@blutec.ai
Mobile: 99980 14013`;


const CompactInstanceNameField = ({ value, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tempValue, setTempValue] = useState(value || "");
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    setTempValue(value || "");
  }, [value]);

  const handleEdit = () => {
    setTempValue(value || "");
    setIsEditing(true);
    setJustSaved(false);
  };

  const handleCancel = () => {
    setTempValue(value || "");
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(tempValue);
      setIsEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (error) {
      setTempValue(value || "");
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          className="flex-1 rounded-md px-2 py-1 text-sm text-gray-900 border border-gray-300 bg-white focus:outline-none focus:border-[#01CC54] focus:ring-1 focus:ring-[#01CC54]"
          placeholder="Enter instance name..."
          autoFocus
        />
        <motion.button
          onClick={handleCancel}
          className="p-1 text-gray-500 hover:text-gray-700 rounded"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <X size={14} />
        </motion.button>
        <motion.button
          onClick={handleSave}
          disabled={isSaving}
          className={`px-2 py-1 rounded text-xs font-medium ${
            isSaving
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-[#048AAD] text-white hover:bg-[#048AAD]/90"
          }`}
          whileHover={!isSaving ? { scale: 1.05 } : {}}
          whileTap={!isSaving ? { scale: 0.95 } : {}}
        >
          {isSaving ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Check size={12} />
          )}
        </motion.button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-900 font-medium flex-1 min-w-0 truncate">
        {value || "Not set"}
      </span>
      {justSaved && (
        <motion.span
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-xs text-[#01CC54] flex items-center gap-1"
        >
          <Check size={12} />
          Saved
        </motion.span>
      )}
      <motion.button
        onClick={handleEdit}
        className="p-1 text-gray-500 hover:text-[#01CC54] hover:bg-gray-100 rounded transition-colors"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <Edit size={14} />
      </motion.button>
    </div>
  );
};


const EditableField = ({
  value,
  onChange,
  onSave,
  placeholder,
  rows,
  type = "text",
  label,
  helpText,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [justSaved, setJustSaved] = useState(false);

  const handleEdit = () => {
    setTempValue(value);
    setIsEditing(true);
    setJustSaved(false);
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(tempValue);
      setIsEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000);
    } catch (error) {
      console.error("Save failed:", error);
      setTempValue(value);
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="font-medium text-gray-900">{label}</label>
          <div className="flex items-center space-x-2">
            <motion.button
              onClick={handleCancel}
              className="p-1 text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40"
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
            >
              <X size={14} />
            </motion.button>
            <motion.button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-2.5 py-1 rounded text-xs font-medium shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/40 ${
                isSaving
                  ? "bg-[#048AAD]/60 text-white cursor-not-allowed"
                  : "bg-[#048AAD] text-white hover:bg-[#048AAD]/90"
              }`}
              whileHover={!isSaving ? { scale: 1.02 } : {}}
              whileTap={!isSaving ? { scale: 0.98 } : {}}
            >
              {isSaving ? (
                <>
                  <Loader2 size={12} className="inline mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={12} className="inline mr-1" />
                  Save
                </>
              )}
            </motion.button>
          </div>
        </div>

        {rows ? (
          <textarea
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            rows={rows}
            className="w-full rounded-lg p-2.5 text-xs text-gray-900 placeholder-gray-400 border border-gray-200 bg-white focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
            placeholder={placeholder}
            autoFocus
          />
        ) : (
          <input
            type={type}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            className="w-full rounded-lg p-2.5 text-xs text-gray-900 placeholder-gray-400 border border-gray-200 bg-white focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition"
            placeholder={placeholder}
            autoFocus
          />
        )}

        {helpText && <p className="text-xs text-gray-500">{helpText}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="font-medium text-gray-900 text-xs">{label}</label>
        <motion.button
          onClick={handleEdit}
          className="p-1 text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 rounded transition focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40"
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
        >
          <Edit size={14} />
        </motion.button>
      </div>

      <AnimatePresence>
        <motion.div
          className={`p-2.5 rounded-lg border transition-all duration-200 bg-gray-50 border-gray-200`}
          initial={justSaved ? { scale: 1.02 } : {}}
          animate={justSaved ? { scale: 1 } : {}}
          transition={{ duration: 0.2 }}
        >
          {justSaved && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center text-[#01CC54] text-xs mb-1.5"
            >
              <Check size={12} className="mr-1" />
              Saved successfully!
            </motion.div>
          )}

          <div className={`text-xs ${value ? "text-gray-900" : "text-gray-500"}`}>
            {rows ? (
              <div className="whitespace-pre-wrap">{value || placeholder}</div>
            ) : (
              <span>{value || placeholder}</span>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {helpText && <p className="text-xs text-gray-400">{helpText}</p>}
    </div>
  );
};

const KnowledgeBaseField = ({
  value,
  onChange,
  onSave,
  placeholder,
  label,
  helpText,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [justSaved, setJustSaved] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleEdit = () => {
    setTempValue(value);
    setIsEditing(true);
    setJustSaved(false);
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      onChange({ target: { value: tempValue } });
      await onSave(tempValue);
      setIsEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000);
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const getTruncatedText = (text, maxLines = 3) => {
    if (!text) return placeholder;
    const lines = text.split("\n");
    if (lines.length > maxLines && !isExpanded) {
      return lines.slice(0, maxLines).join("\n") + "...";
    }
    return text;
  };

  const shouldShowExpandButton = value && value.split("\n").length > 3;

  if (isEditing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="font-medium text-gray-900">{label}</label>
          <div className="flex items-center space-x-2">
            <motion.button
              onClick={handleCancel}
              className="p-1 text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40"
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
            >
              <X size={14} />
            </motion.button>
            <motion.button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-2.5 py-1 rounded text-xs font-medium shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/40 ${
                isSaving
                  ? "bg-[#048AAD]/60 text-white cursor-not-allowed"
                  : "bg-[#048AAD] text-white hover:bg-[#048AAD]/90"
              }`}
              whileHover={!isSaving ? { scale: 1.02 } : {}}
              whileTap={!isSaving ? { scale: 0.98 } : {}}
            >
              {isSaving ? (
                <>
                  <Loader2 size={12} className="inline mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={12} className="inline mr-1" />
                  Save
                </>
              )}
            </motion.button>
          </div>
        </div>

        <textarea
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          rows={6}
          className="w-full rounded-lg p-2.5 text-xs text-gray-900 placeholder-gray-500 border border-gray-200 bg-white focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition resize-y min-h-[100px] max-h-[250px]"
          placeholder={placeholder}
          autoFocus
        />

        {helpText && <p className="text-xs text-gray-500">{helpText}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="font-medium text-gray-900">{label}</label>
        <motion.button
          onClick={handleEdit}
          className="p-1 text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 rounded transition focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40"
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
        >
          <Edit size={14} />
        </motion.button>
      </div>

      <AnimatePresence>
        <motion.div
          className="rounded-lg border bg-gray-50 border-gray-200 transition-all duration-200"
          initial={justSaved ? { scale: 1.02 } : {}}
          animate={justSaved ? { scale: 1 } : {}}
          transition={{ duration: 0.2 }}
        >
          {justSaved && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center text-emerald-300 text-sm p-3 pb-0"
            >
              <Check size={14} className="mr-1" />
              Saved successfully!
            </motion.div>
          )}

          <div className="p-3">
            <div
              className={`text-sm whitespace-pre-wrap ${
                value ? "text-gray-900" : "text-gray-500"
              }`}
            >
              {getTruncatedText(value)}
            </div>

            {shouldShowExpandButton && (
              <motion.button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-2 text-xs text-[#01CC54] hover:text-[#01CC54]/80 font-medium focus:outline-none focus:ring-2 focus:ring-[#01CC54]/30 rounded px-1"
                whileHover={{ scale: 1.02 }}
              >
                {isExpanded ? "Show Less" : "Show More"}
              </motion.button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {helpText && <p className="text-xs text-gray-400">{helpText}</p>}
    </div>
  );
};

const TasksAndRulesField = ({
  value,
  onChange,
  onSave,
  placeholder,
  label,
  helpText,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [justSaved, setJustSaved] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleEdit = () => {
    setTempValue(value);
    setIsEditing(true);
    setJustSaved(false);
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      onChange({ target: { value: tempValue } });
      await onSave(tempValue);
      setIsEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000);
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const getTruncatedText = (text, maxLines = 3) => {
    if (!text) return placeholder;
    const lines = text.split("\n");
    if (lines.length > maxLines && !isExpanded) {
      return lines.slice(0, maxLines).join("\n") + "...";
    }
    return text;
  };

  const shouldShowExpandButton = value && value.split("\n").length > 3;

  // Editing Mode
  if (isEditing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="font-medium text-gray-900">{label}</label>
          <div className="flex items-center space-x-2">
            <motion.button
              onClick={handleCancel}
              className="p-1 text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40"
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
            >
              <X size={14} />
            </motion.button>
            <motion.button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-2.5 py-1 rounded text-xs font-medium shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/40 ${
                isSaving
                  ? "bg-[#048AAD]/60 text-white cursor-not-allowed"
                  : "bg-[#048AAD] text-white hover:bg-[#048AAD]/90"
              }`}
              whileHover={!isSaving ? { scale: 1.02 } : {}}
              whileTap={!isSaving ? { scale: 0.98 } : {}}
            >
              {isSaving ? (
                <>
                  <Loader2 size={12} className="inline mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={12} className="inline mr-1" />
                  Save
                </>
              )}
            </motion.button>
          </div>
        </div>

        <div>
          <textarea
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            rows={8}
            className="w-full rounded-lg p-2.5 text-xs text-gray-900 placeholder-gray-500 border border-gray-200 bg-white focus:outline-none focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition resize-y min-h-[120px] max-h-[320px]"
            placeholder={placeholder}
            autoFocus
          />
          {helpText && <p className="text-xs text-gray-400 mt-2">{helpText}</p>}
        </div>
      </div>
    );
  }

  // Display Mode
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="font-medium text-gray-900">{label}</label>
        <motion.button
          onClick={handleEdit}
          className="p-1 text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 rounded transition focus:outline-none focus:ring-2 focus:ring-[#01CC54]/40"
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
        >
          <Edit size={14} />
        </motion.button>
      </div>

      <AnimatePresence>
        <motion.div
          className="rounded-lg border bg-gray-50 border-gray-200 transition-all duration-200"
          initial={justSaved ? { scale: 1.02 } : {}}
          animate={justSaved ? { scale: 1 } : {}}
          transition={{ duration: 0.2 }}
        >
          {justSaved && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center text-emerald-300 text-sm p-3 pb-0"
            >
              <Check size={14} className="mr-1" />
              Saved successfully!
            </motion.div>
          )}

          <div className="p-3">
            <div
              className={`text-sm whitespace-pre-wrap ${
                value ? "text-gray-900" : "text-gray-500"
              }`}
            >
              {getTruncatedText(value)}
            </div>

            {shouldShowExpandButton && (
              <motion.button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-2 text-xs text-[#01CC54] hover:text-[#01CC54]/80 font-medium focus:outline-none focus:ring-2 focus:ring-[#01CC54]/30 rounded px-1"
                whileHover={{ scale: 1.02 }}
              >
                {isExpanded ? "Show Less" : "Show More"}
              </motion.button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {helpText && <p className="text-xs text-gray-400">{helpText}</p>}
    </div>
  );
};

const ConfigurationTab = (props) => {
  const {
    instanceId,
    authToken,
    tokenExpiry,
    username,
    showToken,
    setShowToken,
    instanceName,
    updateInstanceName,
    safeMode,
    setSafeMode,
    handleSafeModeChange,
    socket,
    qrCode,
    whatsappStatus,
    statusDetails,
    isLoggedIn,
    isLoggingIn,
    isLoggingOut,
    lastQrTime,
    connectionInfo,
    qrLoading,
    scanningLoader,
    handleRequestQr,
    handleLogout,
    copyToClipboard,
    setKnowledgeBase,
    saveKnowledgeBase,
    chatbotEnabled,
    setChatbotEnabled,
    toggleChatbot,
    knowledgeBase,
    replyGroup,
    setReplyGroup,
    toggleReplyGroup,
    voiceTranscription,
    setVoiceTranscription,
    toggleVoiceTranscription,
    language,
    setLanguage,
    role,
    setRole,
    tone,
    setTone,
    updateLanguage,
    updateRole,
    updateTone,
    languageOptions,
    updateReplyToBroadcastOnly,
    replyToBoardcastOnly,
    setReplyToBoardcastOnly,
    ai_reply_days,
    setAiReplyDays,
    ai_reply_start_time,
    setAiReplyStartTime,
    ai_reply_end_time,
    setAiReplyEndTime,
    updateAiReplyScheduleAPI,
    ai_timezone,
    setAiTimezone,
    outOfContextReply,
    setOutOfContextReply,
    updateOutOfContextReply,
    handleRestart,
    isRestarting,
    tasksAndRules,
    setTasksAndRules,
    updateTasksAndRules,
    webhookUrl,
    setWebhookUrl,
    updateWebhookUrl,
    webhookEnabled,
    setWebhookEnabled,
    updateWebhookEnabled,
    generateConfigFromWebsite,
    isGeneratingConfig,
  } = props;

  const [websiteUrl, setWebsiteUrl] = useState("");
  const [generateStatus, setGenerateStatus] = useState(null);

  const handleGenerateFromWebsite = async (e) => {
    e.preventDefault();
    if (!generateConfigFromWebsite) return;

    if (!websiteUrl.trim()) {
      setGenerateStatus({
        type: "error",
        message: "Enter a website URL to continue",
      });
      return;
    }

    setGenerateStatus(null);
    const result = await generateConfigFromWebsite(websiteUrl.trim());

    if (result?.success) {
      setGenerateStatus({
        type: "success",
        message:
          result.message ||
          "Knowledge base and tasks updated from your website",
      });
      setWebsiteUrl("");
    } else {
      setGenerateStatus({
        type: "error",
        message:
          result?.message || "Unable to generate configuration. Try again.",
      });
    }
  };

  return (
    <div className="space-y-3 text-gray-900">
      {/* Restarting Loading Banner */}
      <AnimatePresence>
        {isRestarting && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-gradient-to-r from-amber-50 via-amber-50 to-[#01CC54]/10 border border-amber-300 rounded-lg p-3 shadow-lg"
          >
            <div className="flex items-center space-x-3">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                className="flex-shrink-0"
              >
                <RefreshCw size={20} className="text-amber-600" />
              </motion.div>
              <div className="flex-1">
                <h5 className="font-semibold text-gray-900 mb-0.5 text-sm">
                  Restarting Instance...
                </h5>
                <p className="text-xs text-amber-700">
                  Please wait while we restart your WhatsApp instance. This may
                  take a few moments.
                </p>
              </div>
              <motion.div
                className="flex space-x-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {[1, 2, 3].map((dot) => (
                  <motion.div
                    key={dot}
                    className="w-2 h-2 bg-amber-600 rounded-full"
                    animate={{ scale: [1, 1.5, 1] }}
                    transition={{
                      repeat: Infinity,
                      duration: 1,
                      delay: dot * 0.2,
                    }}
                  />
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div>
        <h3 className="text-base font-semibold mb-1.5 flex items-center">
          <Settings size={18} className="mr-1.5 text-[#01CC54]" />
          Instance Configuration
        </h3>
        <p className="text-xs text-gray-600">
          Manage your WhatsApp instance settings, connection status, and API
          configuration
        </p>
      </div>

       {/* Instance Name */}
       <div className="border border-gray-200 rounded-lg p-2.5 shadow-sm bg-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Smartphone size={14} className="text-[#01CC54]" />
            <span className="text-xs font-medium text-gray-700">Instance Name:</span>
          </div>
          <div className="flex-1 min-w-0">
            <CompactInstanceNameField
              value={instanceName}
              onSave={updateInstanceName}
            />
          </div>
        </div>
      </div>

      {/* Connection Status */}
      <div>
        <h4 className="text-sm font-semibold mb-1.5">Connection Status</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          {/* Auth Status */}
          <div className="border border-gray-200 rounded-lg p-2.5 text-center bg-white shadow-lg">
            <div className="text-xs text-gray-600 mb-1.5 font-medium">
              Authentication
            </div>
            <div
              className={`text-xs font-medium px-2.5 py-1.5 rounded-lg border ${
                authToken
                  ? "bg-[#01CC54]/10 text-[#01CC54] border-[#01CC54]/30"
                  : "bg-amber-50 text-amber-700 border-amber-300"
              }`}
            >
              {authToken ? "✓ Ready" : "⏳ Loading..."}
            </div>
          </div>

          {/* Socket */}
          <div className="border border-gray-200 rounded-lg p-2.5 text-center bg-white shadow-lg">
            <div className="text-xs text-gray-600 mb-1.5 font-medium">
              Server Connection
            </div>
            <div
              className={`flex items-center justify-center text-xs font-medium px-2.5 py-1.5 rounded-lg border ${
                connectionInfo.socketConnected
                  ? "bg-[#01CC54]/10 text-[#01CC54] border-[#01CC54]/30"
                  : "bg-rose-50 text-rose-700 border-rose-300"
              }`}
            >
              {connectionInfo.socketConnected ? (
                <Wifi size={14} className="mr-1" />
              ) : (
                <WifiOff size={14} className="mr-1" />
              )}
              {connectionInfo.socketConnected ? "Connected" : "Disconnected"}
            </div>
          </div>

          {/* WhatsApp Status */}
          <div className="border border-gray-200 rounded-lg p-2.5 text-center bg-white shadow-lg">
            <div className="text-xs text-gray-600 mb-1.5 font-medium">
              WhatsApp Status
            </div>
            <div
              className={`text-xs font-medium px-2.5 py-1.5 rounded-lg flex items-center justify-center border ${
                isLoggedIn
                  ? "bg-[#01CC54]/10 text-[#01CC54] border-[#01CC54]/30"
                  : "bg-rose-50 text-rose-700 border-rose-300"
              }`}
            >
              {scanningLoader && (
                <Loader2 size={14} className="mr-1 animate-spin" />
              )}
              {!scanningLoader &&
                (isLoggedIn ? (
                  <CheckCircle size={14} className="mr-1" />
                ) : (
                  <AlertCircle size={14} className="mr-1" />
                ))}
              {isLoggedIn ? "Logged In" : "Not Logged In"}
            </div>
          </div>

          {/* Instance Ready */}
          <div className="border border-gray-200 rounded-lg p-2.5 text-center bg-white shadow-lg">
            <div className="text-xs text-gray-600 mb-1.5 font-medium">
              Instance Status
            </div>
            <div
              className={`text-xs font-medium px-2.5 py-1.5 rounded-lg border ${
                connectionInfo.instanceReady
                  ? "bg-[#01CC54]/10 text-[#01CC54] border-[#01CC54]/30"
                  : "bg-amber-50 text-amber-700 border-amber-300"
              }`}
            >
              {connectionInfo.instanceReady ? "✓ Ready" : "⚠ Not Ready"}
            </div>
          </div>
        </div>
      </div>

      {/* API Token */}
      <div className="border border-gray-200 rounded-lg p-3 shadow-lg bg-white">
        <h4 className="text-sm font-semibold mb-3 flex items-center">
          <Shield size={16} className="mr-1.5 text-[#01CC54]" />
          API Authentication
        </h4>
        <div className="space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              API Token
            </label>
            <div className="flex items-center space-x-2">
              <div className="flex-1 flex items-center space-x-1.5">
                <div className="bg-gray-50 border border-gray-200 px-2.5 py-1.5 rounded-lg font-mono text-xs overflow-hidden flex-1 text-gray-900">
                  <span className="truncate block">
                    {showToken
                      ? authToken || "Loading..."
                      : "••••••••••••••••••••••••••••••••"}
                  </span>
                </div>
                <button
                  onClick={() => setShowToken(!showToken)}
                  title={showToken ? "Hide token" : "Show token"}
                  className="p-1.5 text-gray-600 hover:text-[#01CC54] hover:bg-gray-100 rounded-lg transition-colors"
                  type="button"
                >
                  {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                {showToken && authToken && (
                  <button
                    className="p-1.5 text-[#01CC54] hover:text-[#01CC54]/80 hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={() => copyToClipboard(authToken, "Token")}
                    title="Copy token"
                    type="button"
                  >
                    <Copy size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 p-2.5 rounded-lg">
            <div className="flex items-start space-x-2">
              <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900 mb-1 text-xs">Token Information</p>
                <ul className="space-y-0.5 text-xs">
                  <li>
                    • Username:{" "}
                    <span className="font-mono text-gray-700">
                      {username || "Loading..."}
                    </span>
                  </li>
                  <li>
                    • Instance ID:{" "}
                    <span className="font-mono text-gray-700">
                      {instanceId}
                    </span>
                  </li>
                  {tokenExpiry && (
                    <li>
                      • Expires:{" "}
                      <span className="font-mono text-gray-700">
                        {new Date(tokenExpiry).toLocaleString()}
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Connection */}
      <div className="border border-gray-200 rounded-lg p-3 shadow-lg bg-white">
        <h4 className="text-sm font-semibold mb-3 flex items-center">
          <Smartphone size={16} className="mr-1.5 text-[#01CC54]" />
          WhatsApp Connection
        </h4>

        {/* Guide */}
        {!isLoggedIn && !qrCode && !qrLoading && (
          <motion.div
            className="border border-gray-200 rounded-lg p-3 mb-2 bg-gray-50"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-full ring-1 ring-gray-200 bg-white">
                  <Smartphone size={20} className="text-[#01CC54]" />
                </div>
                <div>
                  <h5 className="text-base font-semibold mb-0.5 text-gray-900">
                    Connect Your WhatsApp
                  </h5>
                  <p className="text-gray-600 text-xs">
                    Generate a QR code and scan it with your WhatsApp mobile app
                    to establish connection
                  </p>
                </div>
              </div>
              <motion.button
                onClick={handleRequestQr}
                disabled={
                  !connectionInfo.socketConnected ||
                  isLoggingIn ||
                  qrLoading ||
                  isRestarting
                }
                className={`flex items-center px-3 py-1.5 rounded-lg font-medium transition-all text-xs ${
                  !connectionInfo.socketConnected || isLoggingIn || qrLoading
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-[#048AAD] text-white hover:bg-[#048AAD]/90 shadow-lg hover:shadow-xl"
                }`}
                whileHover={
                  !(!connectionInfo.socketConnected || isLoggingIn || qrLoading)
                    ? { scale: 1.05 }
                    : {}
                }
                whileTap={
                  !(!connectionInfo.socketConnected || isLoggingIn || qrLoading)
                    ? { scale: 0.95 }
                    : {}
                }
                type="button"
              >
                <QrCode size={16} className="mr-1.5" />
                Generate QR Code
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* QR Loading */}
        <AnimatePresence>
          {qrLoading && (
            <motion.div
              className="border border-gray-200 rounded-lg p-3 text-center mb-2 bg-gray-50"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col items-center">
                <motion.div
                  className="w-12 h-12 border-4 border-[#01CC54]/40 border-t-[#01CC54] rounded-full mb-3"
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                />
                <h5 className="text-base font-semibold mb-1.5 text-gray-900">
                  Generating QR Code
                </h5>
                <p className="text-xs text-gray-600">
                  Please wait while we create your WhatsApp QR code...
                </p>
                <motion.div
                  className="mt-4 flex space-x-1"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  {[1, 2, 3].map((dot) => (
                    <motion.div
                      key={dot}
                      className="w-2 h-2 bg-[#01CC54] rounded-full"
                      animate={{ scale: [1, 1.5, 1] }}
                      transition={{
                        repeat: Infinity,
                        duration: 1,
                        delay: dot * 0.2,
                      }}
                    />
                  ))}
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* QR Display */}
        <AnimatePresence>
          {qrCode && (
            <motion.div
              className="border border-gray-200 rounded-lg p-3 text-center mb-2 bg-white"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <h5 className="text-base font-semibold mb-3 text-gray-900">
                Scan QR Code to Connect WhatsApp
              </h5>

              <div className="relative inline-block mb-2.5">
                <div className="p-3 bg-white rounded-xl border-2 border-[#01CC54]/40 shadow-lg">
                  <img
                    src={qrCode}
                    alt="WhatsApp QR Code"
                    className="w-48 h-48 object-contain"
                  />
                </div>

                {scanningLoader && (
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center bg-white/95 rounded-xl"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="text-center">
                      <motion.div
                        className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-3"
                        animate={{ rotate: 360 }}
                        transition={{
                          repeat: Infinity,
                          duration: 1,
                          ease: "linear",
                        }}
                      />
                      <p className="text-sm font-medium text-blue-600">
                        Processing scan...
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Instructions */}
              <div className="bg-gray-50 rounded-lg p-3 text-left border border-gray-200">
                <h6 className="font-medium mb-2 text-gray-900 text-xs">How to scan:</h6>
                <div className="space-y-1.5 text-xs text-gray-600">
                  <div className="flex items-center space-x-2">
                    <span className="w-5 h-5 bg-[#048AAD] text-white rounded-full flex items-center justify-center text-xs font-bold">
                      1
                    </span>
                    <span>Open WhatsApp on your phone</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="w-5 h-5 bg-[#048AAD] text-white rounded-full flex items-center justify-center text-xs font-bold">
                      2
                    </span>
                    <span>Go to Settings → Linked Devices</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="w-5 h-5 bg-[#048AAD] text-white rounded-full flex items-center justify-center text-xs font-bold">
                      3
                    </span>
                    <span>Tap "Link a Device" and scan this QR code</span>
                  </div>
                </div>
              </div>

              {/* Timer */}
              <div className="mt-3 space-y-1.5">
                <div className="flex items-center justify-center text-xs text-amber-700 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-300">
                  <Clock size={12} className="mr-1.5" />
                  QR code expires in 60 seconds
                </div>
                {lastQrTime && (
                  <p className="text-xs text-gray-500">
                    Generated: {new Date(lastQrTime).toLocaleTimeString()}
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Logged in: Disconnect */}
        {isLoggedIn && (
          <div className="bg-[#01CC54]/10 border border-[#01CC54]/30 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2.5">
                <div className="w-9 h-9 bg-[#01CC54] rounded-full flex items-center justify-center">
                  <CheckCircle size={18} className="text-white" />
                </div>
                <div>
                  <h5 className="font-medium text-gray-900 text-sm">WhatsApp Connected</h5>
                  <p className="text-xs text-[#01CC54]">
                    Your WhatsApp is successfully connected and ready to use
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <motion.button
                  onClick={handleLogout}
                  disabled={!isLoggedIn || isLoggingOut}
                  className={`flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    isLoggingOut
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-500 text-white shadow-md hover:shadow-lg"
                  }`}
                  whileHover={!isLoggingOut ? { scale: 1.02 } : {}}
                  whileTap={!isLoggingOut ? { scale: 0.98 } : {}}
                  type="button"
                >
                  {isLoggingOut ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <ArrowLeft size={14} className="mr-1.5" />
                  )}
                  {isLoggingOut ? "Logging out..." : "Disconnect"}
                </motion.button>

                <motion.button
                  onClick={handleRestart}
                  disabled={!isLoggedIn || isRestarting}
                  className={`flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    isRestarting
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-yellow-600 hover:bg-yellow-700 text-white shadow-md hover:shadow-lg"
                  }`}
                  whileHover={!isRestarting ? { scale: 1.02 } : {}}
                  whileTap={!isRestarting ? { scale: 0.98 } : {}}
                  type="button"
                >
                  {isRestarting ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCw size={14} className="mr-1.5" />
                  )}
                  {isRestarting ? "Restarting..." : "Restart"}
                </motion.button>
              </div>
            </div>
          </div>
        )}

        {/* Status details */}
        {statusDetails && (
          <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong className="text-gray-900">Status:</strong> {statusDetails}
            </p>
          </div>
        )}
      </div>

      {/* Instance Settings */}
      {isLoggedIn && (
        <div className="border border-gray-200 rounded-lg p-3 shadow-lg bg-white">
          <h4 className="text-sm font-semibold mb-3 text-gray-900">Instance Settings</h4>

          {/* Webhook Toggle */}
          <div className="flex items-cente mb-2 justify-between p-2.5 rounded-lg bg-gray-50 border border-gray-200">
            <div className="flex items-center space-x-2.5">
              <Globe size={18} className="text-[#01CC54]" />
              <div>
                <div className="font-medium text-gray-900 text-sm">Enable Webhook</div>
                <div className="text-xs text-gray-600">
                  Receive all incoming messages (groups and personal chats) via webhook
                </div>
              </div>
            </div>
            <SafeModeSwitch
              enabled={webhookEnabled}
              onChange={(v) => {
                setWebhookEnabled(v);
                updateWebhookEnabled(v);
              }}
            />
          </div>

          {/* Webhook URL Configuration - Only show when enabled */}
          {webhookEnabled && (
            <div className="p-3 my-2 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex items-start space-x-2.5">
                <Globe size={18} className="text-[#01CC54] mt-0.5" />
                <div className="flex-1">
                  <EditableField
                    label="Webhook URL"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    onSave={updateWebhookUrl}
                    type="url"
                    placeholder="https://your-server.com/webhook"
                    helpText="Configure a webhook URL to receive all incoming messages (groups and personal chats) via HTTP POST. Leave empty to disable webhook notifications."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Safe Mode */}
          <div className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 border border-gray-200">
            <div className="flex items-center space-x-2.5">
              <Shield size={18} className="text-[#01CC54]" />
              <div>
                <div className="font-medium text-gray-900 text-sm">Safe Mode</div>
                <div className="text-xs text-gray-600">
                  Prevents WhatsApp blocking and optimizes message delivery
                </div>
              </div>
            </div>
            <SafeModeSwitch
              enabled={safeMode}
              onChange={(v) => {
                setSafeMode(v);
                handleSafeModeChange(v);
              }}
            />
          </div>

        

      {/* AI Chatbot Toggle */}
      <div className="flex items-center justify-between p-2.5 mt-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="flex items-center space-x-2.5">
              <Bot size={18} className="text-[#01CC54]" />
              <div>
                <div className="font-medium text-gray-900 text-sm">Enable AI Chatbot</div>
                <div className="text-xs text-gray-600">
                  Allow the chatbot to automatically reply to all chats
                </div>
              </div>
            </div>
            <SafeModeSwitch
              enabled={chatbotEnabled}
              onChange={(v) => {
                setChatbotEnabled(v);
                toggleChatbot(v);
              }}
            />
          </div>

          {chatbotEnabled && (
            <AiReplyScheduleField
              aiReplyDays={ai_reply_days || []}
              aiReplyStartTime={ai_reply_start_time || ""}
              aiReplyEndTime={ai_reply_end_time || ""}
              aiTimezone={ai_timezone}
              onSave={async (data) => {
                await updateAiReplyScheduleAPI(data);
                setAiReplyDays(data.ai_reply_days);
                setAiReplyStartTime(data.ai_reply_start_time);
                setAiReplyEndTime(data.ai_reply_end_time);
                setAiTimezone(data.ai_timezone);
              }}
            />
          )}

          {/* Conditional toggles */}
          {chatbotEnabled && (
            <>
            
            <div className="flex items-center justify-between p-2.5 mt-3 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center space-x-2.5">
                  <Shield size={18} className="text-[#01CC54]" />
                  <div>
                    <div className="font-medium text-gray-900 text-sm">
                      Reply to Broadcast Only
                    </div>
                    <div className="text-xs text-gray-600">
                      When enabled, the instance will only reply to messages
                      that are part of a broadcast.
                    </div>
                  </div>
                </div>
                <SafeModeSwitch
                  enabled={replyToBoardcastOnly}
                  onChange={(v) => {
                    setReplyToBoardcastOnly(v);
                    updateReplyToBroadcastOnly(v);
                  }}
                />
              </div>
              <div className="flex items-center justify-between p-2.5 mt-3 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center space-x-2.5">
                  <Bot size={18} className="text-[#01CC54]" />
                  <div>
                    <div className="font-medium text-gray-900 text-sm">
                      Enable Reply Group
                    </div>
                    <div className="text-xs text-gray-600">
                      Enable or disable the reply grouping feature
                    </div>
                  </div>
                </div>
                <SafeModeSwitch
                  enabled={replyGroup}
                  onChange={(v) => {
                    setReplyGroup(v);
                    toggleReplyGroup(v);
                  }}
                />
              </div>

              <div className="flex items-center justify-between p-2.5 mt-3 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center space-x-2.5">
                  <Bot size={18} className="text-[#01CC54]" />
                  <div>
                    <div className="font-medium text-gray-900 flex items-center gap-2 text-sm">
                      Voice Transcription
                    </div>
                    <div className="text-xs text-gray-600">
                      Enable AI to transcribe and respond to voice messages
                    </div>
                  </div>
                </div>
                <SafeModeSwitch
                  enabled={voiceTranscription === 1}
                  onChange={(v) => {
                    setVoiceTranscription(v ? 1 : 0);
                    toggleVoiceTranscription(v ? 1 : 0);
                  }}
                />
              </div>

            </>
          )}
          {/* Website Auto Configuration */}
          <div className="p-3 mt-3 rounded-lg bg-white border border-[#01CC54]/30 shadow-sm">
            <div className="flex items-start space-x-2.5">
              <div className="p-2 rounded-full bg-[#01CC54]/10 text-[#01CC54]">
                <Globe size={18} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h5 className="font-semibold text-sm text-gray-900">
                    Auto-generate knowledge & rules
                  </h5>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#01CC54] bg-[#01CC54]/10 px-2 py-0.5 rounded-full uppercase tracking-wide">
                    <Sparkles size={11} /> AI
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-0.5">
                  Paste your public website URL and we’ll fetch the content to
                  populate the knowledge base and AI task rules automatically.
                </p>

                <form
                  className="mt-3 space-y-2"
                  onSubmit={handleGenerateFromWebsite}
                >
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="url"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="https://yourcompany.com"
                      className="flex-1 rounded-md border border-gray-200 px-3 py-2 text-xs focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20"
                    />
                    <motion.button
                      type="submit"
                      disabled={isGeneratingConfig}
                      className={`inline-flex items-center justify-center px-3 py-2 rounded-md text-xs font-semibold text-white ${
                        isGeneratingConfig
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-[#048AAD] hover:bg-[#048AAD]/90 shadow-lg hover:shadow-xl"
                      }`}
                      whileHover={
                        !isGeneratingConfig ? { scale: 1.02 } : undefined
                      }
                      whileTap={
                        !isGeneratingConfig ? { scale: 0.98 } : undefined
                      }
                    >
                      {isGeneratingConfig ? (
                        <>
                          <Loader2 size={14} className="mr-1 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles size={14} className="mr-1" />
                          Generate
                        </>
                      )}
                    </motion.button>
                  </div>
                </form>

                {generateStatus && (
                  <div
                    className={`mt-2 text-xs px-3 py-2 rounded-md border flex items-center gap-1 ${
                      generateStatus.type === "success"
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                        : "bg-rose-50 border-rose-200 text-rose-700"
                    }`}
                  >
                    {generateStatus.type === "success" ? (
                      <CheckCircle size={12} />
                    ) : (
                      <AlertCircle size={12} />
                    )}
                    <span>{generateStatus.message}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Knowledge Base */}
          <div className="p-3 mt-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="flex items-start space-x-2.5">
              <Book size={18} className="text-[#01CC54] mt-0.5" />
              <div className="flex-1">
                <KnowledgeBaseField
                  label="Company Knowledge Base"
                  value={knowledgeBase}
                  onChange={(e) => setKnowledgeBase(e.target.value)}
                  onSave={saveKnowledgeBase}
                  defaultValue={DEFAULT_KNOWLEDGE_BASE}
                  placeholder={DEFAULT_KNOWLEDGE_BASE}
                  helpText="Add your company FAQs, policies, or reference notes, or simply paste a website URL. We'll fetch the site content automatically for the chatbot."
                />
              </div>
            </div>
          </div>

          {/* Tasks and Rules */}
          <div className="p-3 mt-3 rounded-lg bg-gray-50 border border-gray-200">
            <div className="flex items-start space-x-2.5">
              <FileText size={18} className="text-[#01CC54] mt-0.5" />
              <div className="flex-1">
                <TasksAndRulesField
                  label="Tasks and Rules"
                  value={tasksAndRules}
                  onChange={(e) => setTasksAndRules(e.target.value)}
                  onSave={updateTasksAndRules}
                  placeholder="Enter AI tasks and rules for handling conversations...

Examples:
- Always greet customers warmly
- Ask clarifying questions when unclear
- Provide detailed product information
- Escalate to human for refunds
- Respond within business hours only"
                  helpText="Define specific tasks, rules, and guidelines for the AI chatbot to follow during conversations"
                />
              </div>
            </div>
          </div>

    

          {/* Settings Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
            {/* Language */}
            <div className="p-2.5 mt-3 rounded-lg bg-gray-50 border border-gray-200 flex flex-col">
              <div className="flex items-center mb-3">
                <Settings size={18} className="mr-1.5 text-[#01CC54]" />
                <h4 className="text-sm font-semibold text-gray-900">Language Settings</h4>
              </div>
              <EditableField
                label="Supported Languages"
                value={
                  Array.isArray(language) ? language.join(", ") : language || ""
                }
                onChange={(e) => setLanguage(e.target.value)}
                onSave={updateLanguage}
                placeholder="e.g., English, Spanish, French, Hindi"
                helpText="Specify which languages your chatbot can communicate in"
              />
            </div>

            {/* Role */}
            <div className="p-2.5 mt-3 rounded-lg bg-gray-50 border border-gray-200 flex flex-col">
              <div className="flex items-center mb-3">
                <Settings size={18} className="mr-1.5 text-[#01CC54]" />
                <h4 className="text-sm font-semibold text-gray-900">Role Settings</h4>
              </div>
              <EditableField
                label="Role Description"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                onSave={updateRole}
                rows={4}
                placeholder="Enter role description..."
                helpText="Provide a description of the instance role or persona"
              />
            </div>

            {/* Tone */}
            <div className="p-2.5 mt-3 rounded-lg bg-gray-50 border border-gray-200 flex flex-col">
              <div className="flex items-center mb-3">
                <Settings size={18} className="mr-1.5 text-[#01CC54]" />
                <h4 className="text-sm font-semibold text-gray-900">Tone Settings</h4>
              </div>
              <EditableField
                label="Tone Description"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                onSave={updateTone}
                rows={4}
                placeholder="Enter tone description..."
                helpText="Describe the tone or style for chatbot replies"
              />
            </div>

            {/* Out of Context */}
            <div className="p-2.5 mt-3 rounded-lg bg-gray-50 border border-gray-200 flex flex-col">
              <div className="flex items-center mb-3">
                <Settings size={18} className="mr-1.5 text-[#01CC54]" />
                <h4 className="text-sm font-semibold text-gray-900">Out of Context Reply</h4>
              </div>
              <EditableField
                label="Out of Context Reply Text"
                value={outOfContextReply}
                onChange={(e) => setOutOfContextReply(e.target.value)}
                onSave={updateOutOfContextReply}
                rows={4}
                placeholder="Enter reply text for out of context messages..."
                helpText="Response text when chatbot receives out of context messages"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigurationTab;
